import axios from 'axios';
import API_CONFIG from '../config/api.config';
import productService from './productService';
import { customerService } from './customerService';
import { getAuthHeader } from './authService';

// Fetch invoice-based profit/loss report
export const getInvoiceProfitLossReport = async (filters = {}) => {
  try {
    console.log('Fetching invoice profit/loss report with filters:', filters);
    const response = await axios.get(API_CONFIG.BASE_URL + API_CONFIG.ENDPOINTS.INVOICE_PROFIT_LOSS_REPORT, {
      params: filters,
      headers: getAuthHeader(),
      timeout: 30000
    });
    
    console.log('Invoice profit/loss report fetched successfully with', 
               response.data.invoiceDetails ? response.data.invoiceDetails.length : 0, 'invoices');

    return {
      totalInvoices: response.data.totalInvoices || 0,
      totalRevenue: response.data.totalRevenue || 0,
      totalProfit: response.data.totalProfit || 0,
      totalLoss: response.data.totalLoss || 0,
      invoiceDetails: response.data.invoiceDetails || []
    };
  } catch (error) {
    console.error('Error fetching invoice profit/loss report:', error);
    
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Unknown error';
                        
    const enhancedError = new Error(`Failed to fetch invoice profit/loss report: ${errorMessage}`);
    enhancedError.originalError = error;
    enhancedError.status = error.response?.status;
    enhancedError.details = error.response?.data;
    
    throw enhancedError;
  }
};

// Fetch sales report
export const getSalesReport = async (startDate, endDate) => {
  try {
    const response = await axios.get(API_CONFIG.BASE_URL + API_CONFIG.ENDPOINTS.SALES_REPORT, {
      params: { startDate, endDate },
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching sales report:', error);
    throw error;
  }
};

// Fetch purchase report
export const getPurchaseReport = async (startDate, endDate) => {
  try {
    const response = await axios.get(API_CONFIG.BASE_URL + API_CONFIG.ENDPOINTS.PURCHASE_REPORT, {
      params: { startDate, endDate },
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching purchase report:', error);
    throw error;
  }
};

// Fetch inventory report
export const getInventoryReport = async (categoryId) => {
  try {
    const response = await axios.get(API_CONFIG.BASE_URL + API_CONFIG.ENDPOINTS.INVENTORY_REPORT, {
      params: categoryId ? { categoryId } : {},
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching inventory report:', error);
    throw error;
  }
};

// Fetch low stock report
export const getLowStockReport = async () => {
  try {
    const response = await axios.get(API_CONFIG.BASE_URL + API_CONFIG.ENDPOINTS.LOW_STOCK_REPORT, {
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching low stock report:', error);
    throw error;
  }
};

// Fetch all categories for dropdown
export const getAllCategories = async () => {
  try {
    const response = await productService.getCategories({ size: 1000 });
    return response.content || response;
  } catch (error) {
    throw error;
  }
};

// Fetch all customers for dropdown
export const getAllCustomers = async () => {
  try {
    const response = await customerService.getAllCustomers();
    return response.content || response;
  } catch (error) {
    throw error;
  }
};

// Fetch billing report data for a specific date
export const getBillingReportByDate = async (date) => {
  try {
    const salesResponse = await axios.get(`${API_CONFIG.BASE_URL}/sales-orders/date/${date}`, {
      headers: getAuthHeader()
    });
    
    const purchaseResponse = await axios.get(`${API_CONFIG.BASE_URL}/purchase-orders/date/${date}`, {
      headers: getAuthHeader()
    });
    
    console.log('Sales orders response:', salesResponse.data);
    console.log('Purchase orders response:', purchaseResponse.data);
    
    return {
      salesOrders: salesResponse.data || [],
      purchaseOrders: purchaseResponse.data || []
    };
  } catch (error) {
    console.error('Error fetching billing report:', error);
    throw error;
  }
};

// Fetch billing report data for an entire month
export const getBillingReportByMonth = async (startDate, endDate) => {
  try {
    const salesResponse = await axios.get(`${API_CONFIG.BASE_URL}/sales-orders/date-range`, {
      headers: getAuthHeader(),
      params: {
        startDate,
        endDate
      }
    });
    
    const purchaseResponse = await axios.get(`${API_CONFIG.BASE_URL}/purchase-orders/date-range`, {
      headers: getAuthHeader(),
      params: {
        startDate,
        endDate
      }
    });
    
    console.log('Monthly sales orders response:', salesResponse.data);
    console.log('Monthly purchase orders response:', purchaseResponse.data);
    
    return {
      salesOrders: salesResponse.data || [],
      purchaseOrders: purchaseResponse.data || []
    };
  } catch (error) {
    console.error('Error fetching monthly billing report:', error);
    throw error;
  }
};

export const getCounterReport = async (startDate, endDate, locationId) => {
  try {
    const params = { startDate, endDate };
    if (locationId) params.locationId = locationId;

    const response = await axios.get(`${API_CONFIG.BASE_URL}/reports/counter-reports`, {
      headers: getAuthHeader(),
      params
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching counter report:', error);
    throw error;
  }
};

export default {
  getInvoiceProfitLossReport,
  getSalesReport,
  getPurchaseReport,
  getInventoryReport,
  getLowStockReport,
  getAllCategories,
  getAllCustomers,
  getBillingReportByDate,
  getBillingReportByMonth,
  getCounterReport
}; 